// WCO-3103: small fixed bottom-left badge showing what's running.
//   - On PRODUCTION: the CalVer release version (e.g. "v2026.08.10"), baked into the bundle at build
//     time via VITE_APP_VERSION; clicking opens that release's GitHub page (changelog "expand to view").
//   - On any OTHER environment (dev / staging / edge / local): the ENVIRONMENT NAME, taken from the
//     SAME /api/env source the top env-banner uses (`resolvedEnv` prop) — so the badge and the banner
//     are structurally the same source and cannot disagree. No GitHub release exists there, so no link.
//
// It follows the sidebar: COLLAPSED → a compact single-glyph chip (widening to the full label on
// hover) that fits the narrow icon rail; EXPANDED → the full pill. Mirrors the sidebar's collapse.
import { useLanguage } from '../i18n/LanguageContext';

const REPO_RELEASES = 'https://github.com/wayfong/TnB-Production/releases';

interface VersionBadgeProps {
  /** True when the desktop sidebar is collapsed to its icon rail. */
  sidebarCollapsed?: boolean;
  /**
   * The live environment identity from /api/env (the banner's `resolvedEnv`):
   * 'production' | 'staging' | 'dev' | 'edge' | 'local' | undefined (while loading).
   * On 'production' the badge shows the baked CalVer version; otherwise it shows this env name.
   */
  resolvedEnv?: string;
  /**
   * The banner's verification state (`isVerified`). When false, /api/env's `production` classification
   * is only a GUESS the backend couldn't confirm — the top banner shows "Unverified environment". The
   * badge must NOT then claim confirmed production / link a release, or it would contradict the banner
   * (WCO-3103 Copilot review). Defaults to true for backward-compat with callers that don't pass it.
   */
  envVerified?: boolean;
}

export default function VersionBadge({ sidebarCollapsed = false, resolvedEnv, envVerified = true }: VersionBadgeProps) {
  const { t } = useLanguage();
  // Baked at build time — only meaningful (and only used) on production, where it's the CalVer tag.
  const bakedVersion = import.meta.env.VITE_APP_VERSION || 'dev';
  const isRelease = /^v\d{4}\.\d{2}\.\d{2}(\.\d+)?$/.test(bakedVersion);

  // PRODUCTION → show the CalVer release (link to GitHub release). Any other env → show the env name
  // from /api/env (no link). If /api/env hasn't resolved yet, fall back to the baked value so the
  // badge is never blank. A prod build whose baked value isn't a CalVer (misconfig) still won't link.
  // An UNVERIFIED production guess (envVerified===false) is NOT treated as production — it falls
  // through to the env-name display so the badge agrees with the banner's "Unverified" state.
  const onProduction = resolvedEnv === 'production' && envVerified;
  const showRelease = onProduction && isRelease;

  // Friendly display names for the /api/env `cloudEnv` values (which use short codes like 'dev').
  const ENV_LABELS: Record<string, string> = {
    dev: 'development',
    staging: 'staging',
    edge: 'edge',
    local: 'local',
    cloud: 'cloud',
  };
  const envLabel = resolvedEnv
    ? (ENV_LABELS[resolvedEnv] || resolvedEnv)
    : (bakedVersion === 'dev' ? t('common.versionBadgeDev') : bakedVersion);

  const fullLabel = onProduction ? bakedVersion : envLabel;
  const href = showRelease ? `${REPO_RELEASES}/tag/${encodeURIComponent(bakedVersion)}` : undefined;
  // Collapsed glyph: "v" for a release, else the first letter of the env label.
  const collapsedGlyph = showRelease ? 'v' : String(fullLabel).charAt(0).toUpperCase();

  const baseClasses =
    'group fixed bottom-2 z-30 h-6 flex items-center rounded-full text-xs font-mono ' +
    'tracking-tight bg-gray-200/80 text-gray-600 dark:bg-gray-700/80 dark:text-gray-300 ' +
    'backdrop-blur-sm shadow-sm select-none overflow-hidden print:hidden transition-all duration-200';

  // Collapsed: a fixed ~1.5rem circle (v centered) that widens to the full pill on hover. It's
  // centered on the collapsed sidebar's icon rail (w-16 = 64px → center at 2rem; a w-6/1.5rem chip
  // sits at left 1.25rem). On hover it widens rightward. Expanded: the full pill at left-2.
  const sizeClasses = sidebarCollapsed
    ? 'left-[1.25rem] w-6 hover:w-[13rem] justify-center px-0 hover:px-2.5'
    : 'left-2 max-w-[13rem] px-2.5';

  // In the collapsed state the visible text is a single glyph until hovered; expanded shows the full
  // label (the CalVer on prod, or the env name on dev/staging).
  const content = sidebarCollapsed ? (
    <>
      <span className="group-hover:hidden leading-none">{collapsedGlyph}</span>
      <span className="hidden group-hover:inline whitespace-nowrap">{fullLabel}</span>
    </>
  ) : (
    <span className="whitespace-nowrap">{fullLabel}</span>
  );

  if (!href) {
    return (
      <span className={`${baseClasses} ${sizeClasses}`} aria-label={fullLabel}>
        {content}
      </span>
    );
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      title={t('common.versionBadgeTitle')}
      // WCO-3103 (Copilot a11y): when collapsed the visible text is just "v" (the full version is
      // display:none), so without an explicit label a screen reader announces a meaningless link
      // name. Give the anchor an accessible name with both the version and the action.
      aria-label={`${fullLabel} — ${t('common.versionBadgeTitle')}`}
      className={`${baseClasses} ${sizeClasses} hover:bg-gray-300 dark:hover:bg-gray-600 cursor-pointer`}
    >
      {content}
    </a>
  );
}
